import { faSearch, faTimesCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import cx from 'classnames';
import { BZ2_SEARCH_BUTTON, CONFIRM } from 'mk/autogenerated/translations/FacetSelectModal.69f8fd54d7c27532955dad0016502102'
import { PropertyOption } from 'mk2/apps/bazaar/components/FacetSelect';
import PropertyListItem from 'mk2/apps/bazaar/components/PropertyListItem';
import useFacetFiltering from 'mk2/apps/bazaar/hooks/useFacetFiltering';
import { Btn, BtnType } from 'mk2/components/Btn';
import { ClearableInput } from 'mk2/components/ClearableInput';
import { Input } from 'mk2/components/Input';
import { Modal } from 'mk2/components/Modal';
import { Panel } from 'mk2/components/Panel';
import useEventCallback from 'mk2/hooks/useEventCallback';
import React, { useCallback, useState } from 'react';
import { AutoSizer, List, ListRowProps } from 'react-virtualized';
import styles from './FacetSelectModal.mscss';

export const MAX_HEIGHT = 443; // see FacetSelectModal.mscss for calculation

interface OwnProps {
    top: number;
    left: number;
    width?: number;
    show: boolean;
    multiple?: boolean;
    onClose();
}

type Props = OwnProps & FacetSelectOwnProps;

const FacetSelectModal: React.FunctionComponent<Props> = ({ top, left, width, show, onClose, multiple, ...rest }) => {
    return (
        <Modal show={show} className={styles.FacetSelectModal} backdrop style={{ left, top }} onHide={onClose}>
            <Panel className={styles.FacetSelectModal__container} style={{ width }}>
                <FacetSelect {...rest} className={styles.FacetSelectModal__select} />
                {multiple && (
                    <div className={styles.FacetSelectModal__footer}>
                        <Btn type={BtnType.Blue} label={CONFIRM} onClick={onClose} data-cy="confirm-button" />
                    </div>
                )}
            </Panel>
        </Modal>
    );
};

FacetSelectModal.displayName = 'FacetSelectModal';

export default React.memo<Props>(FacetSelectModal);

const ROW_HEIGHT = 44;

interface FacetSelectOwnProps {
    className?: string;
    property: string;
    options: PropertyOption[];
    label: React.ReactNode;
    hasFiltering: boolean;
    alwaysDisplayed?: Array<PropertyOption['value']>;

    isSelected(option: PropertyOption): boolean;
    onItemClick(property: string, value: string);
}

let FacetSelect: React.FunctionComponent<FacetSelectOwnProps> = ({
    className,
    hasFiltering,
    options,
    label,
    property,
    isSelected,
    alwaysDisplayed,
    onItemClick,
}) => {
    const { filterValue, options: filteredOptions, onFilterClear, onFilterChange } = useFacetFiltering({
        options,
        alwaysDisplayed,
        isSelected,
    });
    const [hovering, setHovering] = useState<number>(null);
    const [scrollToIndex, setScrollToIndex] = useState<number | undefined>(undefined);

    const onItemMouseEnter = useEventCallback(
        (prop: string, value: string | number) => {
            setHovering(filteredOptions.findIndex((opt) => opt.value === value));
        },
        [setHovering, filteredOptions],
    );

    const onSearchKeyDown = useEventCallback(
        (event: React.KeyboardEvent<HTMLInputElement>) => {
            const nonDisabledOptions = filteredOptions.filter((opt) => !opt.disabled);
            if (!nonDisabledOptions.length) {
                return;
            }

            if (hovering === null) {
                switch (event.key) {
                    case 'ArrowUp':
                        setHovering(nonDisabledOptions.length - 1);
                        event.preventDefault();
                        break;
                    case 'ArrowDown':
                        setHovering(0);
                        event.preventDefault();
                        break;
                }
            } else {
                let index = hovering;
                switch (event.key) {
                    case 'ArrowUp':
                        do {
                            index--;
                            if (index < 0) {
                                index = nonDisabledOptions.length - 1;
                            }
                        } while (nonDisabledOptions[index].disabled);
                        setHovering(index);
                        setScrollToIndex(index);
                        event.preventDefault();
                        break;
                    case 'ArrowDown':
                        do {
                            index++;
                            if (index >= nonDisabledOptions.length) {
                                index = 0;
                            }
                        } while (nonDisabledOptions[index].disabled);
                        setHovering(index);
                        setScrollToIndex(index);
                        event.preventDefault();
                        break;
                    case 'Enter':
                        const option = nonDisabledOptions[hovering];
                        onItemClick(property, option.value as string);
                        break;
                    default:
                        setHovering(null);
                }
            }
        },
        [setHovering, setScrollToIndex, hovering, filteredOptions, property],
    );

    const rowRenderer = useCallback(
        ({ index, key, style }: ListRowProps) => {
            const option = filteredOptions[index];
            return (
                <PropertyListItem
                    key={key}
                    style={style}
                    isLast={index + 1 === filteredOptions.length}
                    onClick={option.disabled ? null : onItemClick}
                    onMouseEnter={option.disabled ? null : onItemMouseEnter}
                    property={property}
                    value={option.value}
                    label={option.label}
                    count={option.count}
                    selected={isSelected(option)}
                    hover={index === hovering}
                />
            );
        },
        [filteredOptions, property, onItemClick, isSelected, hovering],
    );

    return (
        <div className={cx(styles.FacetSelect, className)}>
            <div className={styles.FacetSelect__headline}>{label}</div>

            {hasFiltering && (
                <ClearableInput
                    className={styles.FacetSelect__filter}
                    icon={<FontAwesomeIcon icon={faSearch} />}
                    clearIcon={<FontAwesomeIcon icon={faTimesCircle} className={styles.FacetSelect__clearIcon} />}
                    hasValue={Boolean(filterValue)}
                    onClear={onFilterClear}
                >
                    <Input
                        className={styles.FacetSelect__filter__input}
                        type="text"
                        onChange={onFilterChange}
                        placeholder={BZ2_SEARCH_BUTTON + '...'}
                        value={filterValue}
                        autoFocus
                        data-cy="facet-select-filter"
                        onKeyDown={onSearchKeyDown}
                    />
                </ClearableInput>
            )}

            <div className={styles.FacetSelect__options}>
                <AutoSizer disableHeight>
                    {({ width }) => (
                        <List
                            rowCount={filteredOptions.length}
                            rowHeight={ROW_HEIGHT}
                            rowRenderer={rowRenderer}
                            width={width}
                            height={Math.min(300, options.length * ROW_HEIGHT)}
                            scrollToIndex={scrollToIndex}
                        />
                    )}
                </AutoSizer>
            </div>
        </div>
    );
};

FacetSelect.displayName = 'FacetSelect';
FacetSelect = React.memo<FacetSelectOwnProps>(FacetSelect);
